import type { ExperienceItem } from "@/lib/types";
import { getCityVisual, getCategoryVisualPool, resolveVisualIntent } from "@/lib/experiences/visuals";
import { CATEGORY_META } from "@/lib/experiences/categoryMeta";
import { isUserFacingValidUrl } from "@/lib/experiences/linkIntegrity";

// ─── Image quality gate ─────────────────────────────────────────────────────
// Real photo first, always validated (never a mock/placeholder/broken URL).
// When there's no usable real photo, fall back to curated stock — and, when a
// destination is known, alternate deterministically across the resolved
// category/intent pool plus the destination's own thumbnail so rows sharing a
// category don't all render the exact same stock photo. No new runtime
// dependency, no fabricated provider photos — every URL here is already
// curated in visuals.ts.
//
// Marketplace Image Quality V2: the fallback is no longer purely category-
// keyed. resolveVisualIntent() first checks the experience's own title (and
// neighborhood) for a more specific visual identity — e.g. a stadium tour
// tagged category="culture" now resolves to the "stadium" pool instead of a
// generic museum image. This is the single shared path used by both
// ExperienceMarketplaceRow and FeaturedShelf.

/** Cheap, stable string hash — deterministic so the same item always maps to
 *  the same variant across renders (no flicker on re-render/sort). */
function stableHash(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return hash;
}

/** Fallback to use when there's no valid real photo — varies by resolved
 *  intent/category and, when known, destination — so a page full of "food"
 *  rows isn't a wall of identical thumbnails.
 *
 *  Marketplace Visual Diversity V3: a 30-destination audit found that even
 *  with curated pools, cards still felt repetitive — largely because the
 *  single city thumbnail was appearing on a third or more of a destination's
 *  cards (it's easy to register "the same photo of Bali" faster than "the
 *  same generic stock photo"). The category/intent pool is now weighted 2x
 *  relative to the city image so the destination photo shows up sometimes,
 *  never dominates. */
export function getExperienceImageFallback(
  exp: ExperienceItem,
  cityHint?: string | null,
  intentOverride?: string | null,
): string | null {
  // Marketplace Media Relevance & Visual Trust V1: mediaRelevance.ts passes
  // a pool key derived from the 8-family activity-intent system (segway,
  // scooter, bike, tapas, cooking, flamenco, stadium, pub_crawl) when it
  // applies — more specific than resolveVisualIntent()'s own 6-bucket
  // title heuristic, so it takes precedence when present.
  const intent = intentOverride ?? resolveVisualIntent(exp.title, exp.neighborhood);
  const pool   = getCategoryVisualPool(intent ?? exp.category);
  const cityImage = cityHint ? getCityVisual(cityHint)?.thumbnailImage ?? null : null;

  const candidates = cityImage ? [...pool, ...pool, cityImage] : pool;
  if (candidates.length === 0) return null;
  const hashInput = `${exp.id || exp.title}::${exp.category}::${cityHint ?? ""}`;
  return candidates[stableHash(hashInput) % candidates.length];
}

/** Best available image for a card/row: a real, validated photo first, the
 *  category/destination fallback second. */
export function getExperienceImageCandidate(exp: ExperienceItem, cityHint?: string | null): string | null {
  if (exp.image_url && isUserFacingValidUrl(exp.image_url)) return exp.image_url;
  return getExperienceImageFallback(exp, cityHint);
}

// Labels for resolved intents — either because the intent isn't a real
// backend category at all (CATEGORY_META has no entry for "stadium"), or
// because the intent-specific phrasing is more honest/specific than the
// generic category label ("cultura local" for a museum/gallery match vs.
// plain "cultura", "mercado local" for a market/food match vs. plain
// "gastronomía").
const INTENT_ALT_LABELS: Readonly<Record<string, string>> = {
  stadium: "estadio",
  culture: "cultura local",
  food:    "mercado local",
};

/**
 * Alt text that is descriptive but never overclaims: a real photo gets the
 * experience's own title, but a stock fallback is labeled as illustrative —
 * it depicts the resolved category/intent, not the specific activity.
 */
export function getExperienceImageAlt(
  exp: ExperienceItem,
  isRealPhoto: boolean,
  displayTitle?: string,
  altLabelOverride?: string | null,
): string {
  if (isRealPhoto) return displayTitle ?? exp.title;
  if (altLabelOverride) return `Imagen ilustrativa de ${altLabelOverride}`;
  const intent = resolveVisualIntent(exp.title, exp.neighborhood);
  const label = intent
    ? (INTENT_ALT_LABELS[intent] ?? CATEGORY_META[intent]?.label.toLowerCase() ?? intent)
    : (CATEGORY_META[exp.category]?.label.toLowerCase() ?? exp.category);
  return `Imagen ilustrativa de ${label}`;
}
